/**
 * 사이트 전역 SEO 상수 및 라우트별 메타데이터 프리셋.
 * 배포 도메인이 바뀌면 SITE_URL 만 수정하면 canonical/OG/sitemap 이 일괄 반영된다.
 */

export const SITE_URL = 'https://www.promptech.co.kr';
export const SITE_NAME = '프람트테크놀로지';
export const SITE_NAME_EN = 'Prompt Technology';

/** 기본(폴백) 메타 — 페이지가 별도 값을 주지 않을 때 사용 */
export const DEFAULT_TITLE = '프람트테크놀로지 | 공공데이터와 AI를 하나로';
export const DEFAULT_DESCRIPTION =
  '프람트테크놀로지는 공공데이터의 개방·구축·품질 고도화부터 LLM 기반 AI 플랫폼(MCP·RAG·AI Agent)까지, 데이터의 전 여정을 설계·운영하는 공공혁신 플랫폼 기업입니다.';
export const DEFAULT_KEYWORDS = [
  '프람트테크놀로지',
  'Prompt Technology',
  '공공데이터',
  '공공데이터 품질',
  'AI 플랫폼',
  'MCP 서버',
  'RAG',
  'AI Agent',
  'Data Infuser',
  'Data Verifier',
  '데이터맵',
].join(', ');

/** OG 공유 기본 이미지 (절대경로는 Seo 컴포넌트에서 SITE_URL 과 합성) */
export const DEFAULT_OG_IMAGE = '/logo192.png';

export interface RouteSeo {
  path: string;
  title: string;
  description: string;
  /** sitemap.xml 우선순위 (0.0 ~ 1.0) */
  priority: number;
}

/** sitemap 및 페이지 메타에 공통으로 쓰는 라우트 정의 */
export const ROUTE_SEO: Record<string, RouteSeo> = {
  main: {
    path: '/',
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    priority: 1.0,
  },
  data: {
    path: '/data',
    title: '공공데이터 — 개방·구축·정제·품질검증',
    description:
      '데이터 개방·구축·정제·품질 검증까지. 프람트테크놀로지가 공공의 데이터를 신뢰할 수 있는 자산으로 만듭니다.',
    priority: 0.9,
  },
  ai: {
    path: '/ai',
    title: 'AI 플랫폼 — MCP·RAG·AI Agent',
    description: '공공 MCP 서버 구축. LLM·RAG·AI Agent·Agent2Agent·AgentGateway로 공공서비스를 지능형으로 혁신합니다.',
    priority: 0.9,
  },
  aboutus: {
    path: '/aboutus',
    title: '회사소개',
    description: '프람트테크놀로지는 공공분야 데이터·AI 전문 기업입니다. 회사 비전과 사업 영역을 소개합니다.',
    priority: 0.7,
  },
  histories: {
    path: '/histories',
    title: '연혁',
    description: '프람트테크놀로지의 발자취 — 주요 사업 수행 이력과 성장 과정을 소개합니다.',
    priority: 0.5,
  },
  patents: {
    path: '/patents',
    title: '특허 · 인증',
    description: '프람트테크놀로지가 보유한 특허 및 인증 현황을 소개합니다.',
    priority: 0.5,
  },
  infuser: {
    path: '/products/infuser',
    title: 'Data Infuser — 공공데이터 개방·OpenAPI',
    description:
      'Data Infuser 는 공공데이터를 손쉽게 개방하고 OpenAPI 로 제공하는 프람트테크놀로지의 데이터 개방 솔루션입니다.',
    priority: 0.7,
  },
  aiInfuser: {
    path: '/products/ai-infuser',
    title: 'AI Infuser — AI 에이전트 플랫폼',
    description:
      'AI Infuser 는 게이트웨이·다중 에이전트·MCP·RAG·가드레일을 하나로 묶어 외부 AI를 안전하게 연결·운영하는 프람트테크놀로지의 AI 에이전트 플랫폼입니다.',
    priority: 0.7,
  },
  datamap: {
    path: '/products/datamap',
    title: 'DataMap — 데이터 지도',
    description: 'DataMap 은 데이터 자산을 시각적으로 탐색·연결하는 프람트테크놀로지의 데이터 맵 솔루션입니다.',
    priority: 0.7,
  },
  qufaStudio: {
    path: '/products/qufa-studio',
    title: 'QuFa Studio — 데이터 품질 검증',
    description: 'QuFa Studio 는 공공데이터의 품질을 진단·검증·개선하는 프람트테크놀로지의 데이터 품질 솔루션입니다.',
    priority: 0.7,
  },
};

/** sitemap.xml 생성에 사용할 경로 목록 */
export const SITEMAP_ROUTES = Object.values(ROUTE_SEO);

/**
 * 제품 페이지용 SoftwareApplication 구조화 데이터(JSON-LD) 생성기.
 * 답변 엔진(GEO/AEO)·검색엔진이 제품을 엔티티로 인식하도록 돕는다.
 */
export function buildProductLd(routeKey: keyof typeof ROUTE_SEO, name: string) {
  const route = ROUTE_SEO[routeKey];
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: `${SITE_URL}${route.path}`,
    description: route.description,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: `${SITE_URL}/`,
    },
  };
}
